import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { supabase } from '../lib/supabaseClient'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [userRole, setUserRole] = useState(null)

  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [authError, setAuthError] = useState(null)

  async function loadProfile(currentUser) {
    if (!currentUser?.id) {
      setProfile(null)
      setUserRole(null)
      setProfileLoading(false)
      return
    }

    setProfileLoading(true)

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .maybeSingle()

      if (error) {
        throw error
      }

      setProfile(data ?? null)
      setUserRole(data?.role ?? null)

      console.log(
        '[AuthProvider] Profil geladen:',
        currentUser.email,
        data?.role ?? 'keine Rolle',
      )
    } catch (error) {
      console.error(
        '[AuthProvider] Profil konnte nicht geladen werden:',
        error,
      )

      setProfile(null)
      setUserRole(null)
      setAuthError(error)
    } finally {
      setProfileLoading(false)
    }
  }

  useEffect(() => {
    let active = true

    async function initializeAuth() {
      console.log('[AuthProvider] Initialisiere Auth …')

      setAuthLoading(true)
      setAuthError(null)

      try {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession()

        if (error) {
          throw error
        }

        if (!active) return

        setSession(currentSession ?? null)
        setUser(currentSession?.user ?? null)

        /*
         * Wichtig:
         * Auth ist damit grundsätzlich geklärt.
         * Wir beenden authLoading BEVOR weitere
         * Supabase-Abfragen stattfinden.
         */
        setAuthLoading(false)

        if (currentSession?.user) {
          await loadProfile(currentSession.user)
        } else {
          setProfile(null)
          setUserRole(null)
          setProfileLoading(false)
        }
      } catch (error) {
        console.error(
          '[AuthProvider] Auth-Initialisierung fehlgeschlagen:',
          error,
        )

        if (!active) return

        setSession(null)
        setUser(null)
        setProfile(null)
        setUserRole(null)

        setAuthError(error)
        setProfileLoading(false)
        setAuthLoading(false)
      }
    }

    initializeAuth()

    /*
     * WICHTIG:
     *
     * Dieser Callback bleibt bewusst synchron.
     *
     * Innerhalb von onAuthStateChange starten wir
     * keine weitere Supabase-Abfrage mit await.
     */
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!active) return

      console.log(
        '[AuthProvider] Auth-State geändert:',
        event,
        newSession?.user?.email ?? 'kein User',
      )

      setSession(newSession ?? null)
      setUser(newSession?.user ?? null)
      setAuthError(null)
      setAuthLoading(false)

      if (!newSession?.user) {
        setProfile(null)
        setUserRole(null)
        setProfileLoading(false)
        return
      }

      /*
       * Profilabfrage bewusst aus dem Auth-Callback
       * herauslösen.
       */
      setTimeout(() => {
        if (active) {
          loadProfile(newSession.user)
        }
      }, 0)
    })

    return () => {
      active = false
      subscription?.unsubscribe()
    }
  }, [])

  async function signInWithMagicLink(email) {
    const normalizedEmail = email?.trim()

    if (!normalizedEmail) {
      throw new Error('Bitte eine E-Mail-Adresse eingeben.')
    }

    setAuthError(null)

    const redirectTo = `${window.location.origin}/auth/callback`

    const { error } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        emailRedirectTo: redirectTo,
      },
    })

    if (error) {
      setAuthError(error)
      throw error
    }

    return true
  }

  async function signOut() {
    setAuthError(null)

    const { error } = await supabase.auth.signOut()

    if (error) {
      setAuthError(error)
      throw error
    }

    setSession(null)
    setUser(null)
    setProfile(null)
    setUserRole(null)

    setAuthLoading(false)
    setProfileLoading(false)
  }

  async function refreshProfile() {
    if (!user) {
      setProfile(null)
      setUserRole(null)
      setProfileLoading(false)
      return
    }

    await loadProfile(user)
  }

  const isAdmin = userRole === 'admin'
  const isEditor = userRole === 'editor'
  const isPrivileged = isAdmin || isEditor

  const isUser = Boolean(user) && !isPrivileged

  /*
   * RequireRole wartet sowohl auf die Session
   * als auch auf die Rollenabfrage.
   */
  const loading = authLoading || profileLoading

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      userRole,

      loading,
      authLoading,
      profileLoading,
      authError,

      isAuthenticated: Boolean(user),

      isAdmin,
      isEditor,
      isPrivileged,
      isUser,

      signInWithMagicLink,
      signOut,
      refreshProfile,
    }),
    [
      session,
      user,
      profile,
      userRole,
      loading,
      authLoading,
      profileLoading,
      authError,
      isAdmin,
      isEditor,
      isPrivileged,
      isUser,
    ],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthRole() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error(
      'useAuthRole() muss innerhalb eines <AuthProvider> verwendet werden.',
    )
  }

  return context
}

export default AuthProvider